import React from "react";
import { InlineMath } from "react-katex";
import {
  makeSimpleClosedTask,
  normalizeMay2026Variant,
  pick,
  renderClosedTask,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";

// Oryginał CKE zachowuje układ 3 + 4. W wariantach treningowych zmieniamy
// także liczebności grup: łącznie występuje od 5 do 9 różnych liter,
// a grupy celowo nie mają tej samej liczebności.
const VARIANTS = [
  { n1: 2, m1: 3, n2: 3, m2: 5, result: 4.2 },
  { n1: 3, m1: 4, n2: 2, m2: 7, result: 5.2 },
  { n1: 2, m1: 5, n2: 4, m2: 2, result: 3 },
  { n1: 4, m1: 2, n2: 2, m2: 8, result: 4 },
  { n1: 3, m1: 2, n2: 4, m2: 5.5, result: 4 },
  { n1: 4, m1: 3.5, n2: 3, m2: 7, result: 5 },
  { n1: 3, m1: 4, n2: 5, m2: 7, result: 5.875 },
  { n1: 5, m1: 2, n2: 3, m2: 6, result: 3.5 },
  { n1: 4, m1: 2, n2: 5, m2: 5.6, result: 4 },
  { n1: 5, m1: 4.8, n2: 4, m2: 3, result: 4 },
];

const decimalLatex = (value) => String(value).replace(".", "{,}");
const LETTERS = "abcdefghi".split("");

function taskGroups(data) {
  const first = LETTERS.slice(0, data.n1);
  const second = LETTERS.slice(data.n1, data.n1 + data.n2);
  return { first, second, all: [...first, ...second] };
}

const listLatex = (letters) => letters.join(",\\;");
const sumLatex = (letters) => letters.join("+");

function sortedOptions(data, variant) {
  if (variant === "original") {
    return [["A", "3{,}5"], ["B", "3{,}75"], ["C", "4"], ["D", "4{,}25"]];
  }

  // Najpierw bierzemy typowe błędne sposoby liczenia (m.in. zwykłą średnią
  // ze średnich), a brakujące dystraktory uzupełniamy bliskimi wartościami.
  // Set usuwa przypadki, w których dwa sposoby prowadzą do tej samej liczby.
  const candidates = [
    data.result,
    (data.m1 + data.m2) / 2,
    data.result - 0.25,
    data.result + 0.25,
    data.result - 0.5,
    data.result + 0.5,
    data.result - 0.75,
    data.result + 0.75,
  ];
  const values = [];
  candidates.forEach((value) => {
    const rounded = Number(value.toFixed(4));
    if (!values.some((existing) => Math.abs(existing - rounded) < 1e-9)) values.push(rounded);
  });

  return values
    .slice(0, 4)
    .sort((left, right) => left - right)
    .map((value, index) => [["A", "B", "C", "D"][index], decimalLatex(value)]);
}

export function generateTask(options = {}) {
  const variant = normalizeMay2026Variant(options);
  const data = variant === "original" ? { n1: 3, m1: 2, n2: 4, m2: 5.5, result: 4 } : pick(VARIANTS);
  const groups = taskGroups(data);
  const firstSum = Number((data.n1 * data.m1).toFixed(6));
  const secondSum = Number((data.n2 * data.m2).toFixed(6));
  const answer = decimalLatex(data.result);
  return makeSimpleClosedTask({
    type: "MaturaCombinedGroupMeanClosedTask",
    taskNumber: "32",
    variant,
    prompt: null,
    correct: answer,
    wrong: [decimalLatex(data.result + 0.25), decimalLatex(data.result - 0.25), decimalLatex((data.m1 + data.m2) / 2)],
    fixedOptions: sortedOptions(data, variant),
    data,
    tags: ["statystyka", "srednia wazona"],
    explanationSteps: [
      {
        title: "Obliczamy sumę liczb z pierwszej grupy: liczbę elementów mnożymy przez ich średnią.",
        latex: `${sumLatex(groups.first)}=${data.n1}\\cdot${decimalLatex(data.m1)}=${decimalLatex(firstSum)}`,
      },
      {
        title: "W ten sam sposób obliczamy sumę liczb z drugiej grupy.",
        latex: `${sumLatex(groups.second)}=${data.n2}\\cdot${decimalLatex(data.m2)}=${decimalLatex(secondSum)}`,
      },
      {
        title: "Dodajemy obie sumy i dzielimy przez łączną liczbę elementów.",
        latex: `\\bar x=\\frac{${decimalLatex(firstSum)}+${decimalLatex(secondSum)}}{${data.n1}+${data.n2}}=\\frac{${decimalLatex(firstSum + secondSum)}}{${data.n1 + data.n2}}=${answer}`,
      },
    ],
  });
}

export function renderTask({ data }) {
  // Próby zapisane przed wprowadzeniem struktury n1/m1/n2/m2 mają tylko
  // gotowy tekst w polu prompt. Wyświetlamy je starym adapterem zamiast
  // pokazywać użytkownikowi `undefined`.
  if (data?.m1 == null || data?.m2 == null) {
    return renderClosedTask({ data });
  }

  const options = data.options || [];
  const groups = taskGroups(data);
  return (
    <div style={{ lineHeight: 1.55 }}>
      <p style={{ margin: 0 }}>
        Średnia arytmetyczna liczb: <InlineMath math={listLatex(groups.first)} />{" "}jest
        równa <InlineMath math={decimalLatex(data.m1)} />.
        <br />
        Średnia arytmetyczna liczb: <InlineMath math={listLatex(groups.second)} />{" "}
        jest równa <InlineMath math={decimalLatex(data.m2)} />.
      </p>
      <p style={{ margin: "18px 0 0" }}>
        <strong>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</strong>
      </p>
      <p style={{ margin: "18px 0 0" }}>
        Średnia arytmetyczna wszystkich liczb:
        {" "}<InlineMath math={listLatex(groups.all)} />{" "}jest równa
      </p>
      <div style={{ display: "grid", gap: 4, marginTop: 10 }}>
        {options.map((option) => (
          <div key={option.label}>
            <InlineMath math={`${option.label}.\\;${option.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export {
  checkClosedAnswer as checkAnswer,
  renderClosedAnswer as renderAnswer,
  renderClosedExplanation as renderExplanation,
  renderClosedInput as renderInput,
  renderClosedUserAnswer as renderUserAnswer,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";
